import React, { useState, useEffect } from 'react';
import { Spin, Button, message, Tooltip, DatePicker } from 'antd';
import { LeftOutlined, RightOutlined, CloseOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';
import { timeSlotsDB } from '../../services/firebase/database';
import './WeekSlotGrid.css';

const WeekSlotGrid = ({ startDate, onDateChange, onSlotAction, bookings = [] }) => {
    const [loading, setLoading] = useState(false);
    const [slots, setSlots] = useState({}); // Map of date -> array of slots
    const [weekDates, setWeekDates] = useState([]);
    const [timeRows, setTimeRows] = useState([]);

    // Generate times from 8 AM to 8 PM
    const generateTimeRows = () => {
        const times = [];
        for (let i = 8; i <= 20; i++) {
            const hour = i > 12 ? i - 12 : i;
            const ampm = i >= 12 ? 'PM' : 'AM';
            const timeStr = `${i.toString().padStart(2, '0')}:00`;
            const displayTime = `${hour.toString().padStart(2, '0')}:00 ${ampm}`;
            times.push({ id: timeStr, label: displayTime });
        }
        return times;
    };

    useEffect(() => {
        const rows = generateTimeRows();
        setTimeRows(rows);
    }, []);

    useEffect(() => {
        if (startDate) {
            fetchWeekSlots();
            generateWeekDates();
        }
    }, [startDate]);

    const generateWeekDates = () => {
        const current = moment(startDate).startOf('week');
        const dates = [];
        for (let i = 0; i < 7; i++) {
            dates.push(current.clone().add(i, 'days'));
        }
        setWeekDates(dates);
    };

    const fetchWeekSlots = async () => {
        setLoading(true);
        try {
            const weekStart = moment(startDate).startOf('week');
            const weekEnd = moment(startDate).endOf('week');

            const weekSlots = await timeSlotsDB.getSlots(weekStart.format('YYYY-MM-DD'), weekEnd.format('YYYY-MM-DD'));

            const slotsByDate = {};
            weekSlots.forEach(slot => {
                if (!slotsByDate[slot.date]) {
                    slotsByDate[slot.date] = [];
                }
                slotsByDate[slot.date].push(slot);
            });

            setSlots(slotsByDate);
        } catch (error) {
            console.error('Error fetching week slots:', error);
            message.error('Failed to load week schedule');
        } finally {
            setLoading(false);
        }
    };

    const getSlotStatus = (date, timeId) => {
        const dateStr = date.format('YYYY-MM-DD');
        const slotHour = parseInt(timeId.split(':')[0], 10);

        // 1. Match Bookings (Robust Logic)
        const bookingAtSlot = bookings.find(b => {
            if (b.status === 'cancelled') return false;

            // Parse Booking Date
            let bDateStr = '';
            let bDateMoment;

            if (b.preferredDate?.toDate) {
                bDateMoment = moment(b.preferredDate.toDate());
            } else if (moment.isMoment(b.preferredDate)) {
                bDateMoment = b.preferredDate;
            } else {
                bDateMoment = moment(b.preferredDate);
            }

            if (!bDateMoment || !bDateMoment.isValid()) return false;
            if (bDateMoment.format('YYYY-MM-DD') !== dateStr) return false;

            // Parse Booking Time
            let bHour = -1;
            if (b.preferredTime?.toDate) {
                bHour = moment(b.preferredTime.toDate()).hour();
            } else if (typeof b.preferredTime === 'string') {
                // Handle "HH:mm" strings
                const parts = b.preferredTime.trim().split(':');
                if (parts.length > 0) {
                    bHour = parseInt(parts[0], 10);
                }
            }

            return bHour === slotHour;
        });

        if (bookingAtSlot) {
            return { status: 'booked', data: bookingAtSlot, type: 'booking' };
        }

        // 2. Check Slots DB
        const daySlots = slots[dateStr] || [];
        const slot = daySlots.find(s => s.time === timeId);

        if (slot) {
            return { status: slot.status, data: slot, type: 'slot' };
        }

        return null;
    };

    const handleSlotClick = (date, timeId, slotInfo) => {
        const dateStr = date.format('YYYY-MM-DD');
        if (onSlotAction) {
            onSlotAction(dateStr, timeId, slotInfo);
        }
    };

    const renderSlot = (date, timeId) => {
        const slotInfo = getSlotStatus(date, timeId);

        if (!slotInfo) {
            return (
                <div
                    className="slot-box blocked"
                    onClick={() => handleSlotClick(date, timeId, null)}
                >
                    --
                </div>
            );
        }

        if (slotInfo.status === 'booked') {
            return (
                <div
                    className="slot-box booked"
                    onClick={() => handleSlotClick(date, timeId, slotInfo)}
                >
                    <CloseOutlined className="slot-icon" />
                </div>
            );
        }

        if (slotInfo.status === 'available') {
            return (
                <div
                    className="slot-box available"
                    onClick={() => handleSlotClick(date, timeId, slotInfo)}
                >
                </div>
            );
        }

        return (
            <div
                className="slot-box blocked"
                onClick={() => handleSlotClick(date, timeId, slotInfo)}
            >
                --
            </div>
        );
    };

    return (
        <div className="week-slot-grid-container">
            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                <Button icon={<LeftOutlined />} onClick={() => onDateChange(moment(startDate).subtract(1, 'week'))} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        {moment(startDate).startOf('week').format('Do MMM')} - {moment(startDate).endOf('week').format('Do MMM')}
                    </span>
                </div>

                <Button icon={<RightOutlined />} onClick={() => onDateChange(moment(startDate).add(1, 'week'))} />
            </div>

            <Spin spinning={loading}>
                <div style={{ overflowX: 'auto' }}>
                    <table className="week-grid-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                {weekDates.map(date => (
                                    <th key={date.format('YYYY-MM-DD')}>
                                        <div>{date.format('ddd')}</div>
                                        <div style={{ fontSize: '12px', fontWeight: 'normal', color: '#9ca3af' }}>{date.format('DD')}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {timeRows.map(row => (
                                <tr key={row.id}>
                                    <td>{row.label}</td>
                                    {weekDates.map(date => (
                                        <td key={`${date.format('YYYY-MM-DD')}-${row.id}`}>
                                            <div className="slot-cell-content">
                                                {renderSlot(date, row.id)}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Spin>

            <div className="week-grid-legend">
                <div className="legend-item">
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#52c41a', marginRight: '5px', borderRadius: '2px' }}></span> Available
                </div>
                <div className="legend-item">
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#ff4d4f', marginRight: '5px', borderRadius: '2px' }}></span> Booked
                </div>
                <div className="legend-item">
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#d9d9d9', marginRight: '5px', borderRadius: '2px' }}></span> No Slot
                </div>
            </div>
        </div>
    );
};

export default WeekSlotGrid;
